import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatToIST } from "@/utils/dateUtils";
import { toast } from "sonner";
import {
  ServerCog, RefreshCw, Loader2, CheckCircle2, XCircle,
  ChevronDown, ChevronRight, Copy,
} from "lucide-react";

/**
 * Admin-only panel for the order-detail page: shows the Delhivery
 * manifest-creation + get-LRN-detail API calls for ONE order (request +
 * response, success AND failure), newest-first. Backed by
 * GET /api-logs/order/{orderId}/manifest which requires `api_logs:view`, so
 * users without that permission simply see nothing (the panel hides itself).
 */

const CALL_TYPE_STYLE = {
  "Manifest Creation": "bg-teal-50 text-teal-700 border-teal-200",
  "Get LRN (Manifest Status)": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Get LRN Detail (Tracking)": "bg-sky-50 text-sky-700 border-sky-200",
  "Cancel LRN": "bg-amber-50 text-amber-700 border-amber-200",
  "Authentication": "bg-slate-100 text-slate-600 border-slate-200",
};

const prettyJson = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

function CopyButton({ value, label = "JSON" }) {
  const copy = () => {
    const text = prettyJson(value);
    if (!text) return;
    navigator.clipboard?.writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Copy failed"));
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
    >
      <Copy className="w-3 h-3" /> Copy
    </button>
  );
}

function JsonPanel({ title, value }) {
  const text = prettyJson(value);
  if (!text) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
        <CopyButton value={value} label={title} />
      </div>
      <pre className="max-h-72 overflow-auto rounded-md bg-slate-900 px-3 py-2 text-[12px] leading-relaxed text-slate-100 whitespace-pre-wrap break-words">
        {text}
      </pre>
    </div>
  );
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const success = !!log.is_success;
  const badgeClass = CALL_TYPE_STYLE[log.call_type] || "bg-slate-100 text-slate-600 border-slate-200";
  const ts = log.request_time || log.created_at;
  // The manifest-creation "queue" response carries the Delhivery job id — surface it.
  const jobId = log.response_data?.job_id || log.request_payload?.job_id;

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start sm:items-center gap-3 px-3 py-3 text-left hover:bg-slate-50"
      >
        <span className="mt-0.5 sm:mt-0 shrink-0">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={`border ${badgeClass} text-xs font-medium`}>
              {log.call_type || (log.api_provider || "").replace(/^b2c_/, "") || "call"}
            </Badge>
            {success ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                <CheckCircle2 className="w-3.5 h-3.5" /> Success
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                <XCircle className="w-3.5 h-3.5" /> Failed
              </span>
            )}
            {log.status_code != null && (
              <Badge variant="secondary" className="text-[11px]">
                {log.http_method || ""} {log.status_code}
              </Badge>
            )}
            {jobId && (
              <span className="text-[11px] font-mono text-slate-500" title="Delhivery manifest job id">
                Job: {jobId}
              </span>
            )}
          </div>
          {log.error_message && !open && (
            <p className="mt-1 text-xs text-red-600 truncate">{log.error_message}</p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs text-slate-500 whitespace-nowrap">{ts ? formatToIST(ts) : "—"}</p>
          {log.duration_ms != null && (
            <p className="text-[11px] text-slate-400">{Math.round(log.duration_ms)} ms</p>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3 space-y-3">
          {log.api_endpoint && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Endpoint</p>
              <p className="text-[12px] font-mono text-slate-700 break-all">
                {log.http_method && <span className="text-slate-500">{log.http_method} </span>}
                {log.api_endpoint}
              </p>
            </div>
          )}
          {(log.triggered_by || log.triggered_by_role) && (
            <p className="text-xs text-slate-500">
              Triggered by <span className="font-medium text-slate-700">{log.triggered_by || "system"}</span>
              {log.triggered_by_role && <span> ({log.triggered_by_role})</span>}
            </p>
          )}
          {log.error_message && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-red-500 mb-1">Error</p>
              <pre className="max-h-48 overflow-auto rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-700 whitespace-pre-wrap break-words">
                {log.error_message}
              </pre>
            </div>
          )}
          <JsonPanel title="Request payload" value={log.request_payload} />
          <JsonPanel title="Response" value={log.response_data} />
        </div>
      )}
    </div>
  );
}

// Reusable per-order API-log panel. Defaults to the B2B Delhivery manifest/LRN view; pass
// logsUrl + copy to repoint it (e.g. the admin B2C 'Courier API Logs' card).
export default function ManifestApiLogs({
  orderId,
  logsUrl = `${API}/api-logs/order/${orderId}/manifest`,
  title = "Manifest & LRN API Logs",
  description = "Delhivery manifest-creation and get-LRN-detail calls for this order — request & response, success and failure.",
  emptyText = "No manifest or LRN API calls have been logged for this order yet.",
  emptyHint = "Calls appear here after an LRN assignment is attempted.",
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false); // no permission / hard error -> hide the whole panel

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await axios.get(logsUrl);
      setLogs(Array.isArray(res.data) ? res.data : []);
      setHidden(false);
    } catch (e) {
      // 403 => viewer lacks api_logs:view; just hide the panel rather than show an error.
      setHidden(true);
    } finally {
      setLoading(false);
    }
  }, [orderId, logsUrl]);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  return (
    <Card className="border-0 shadow-lg mt-6">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            <ServerCog className="w-5 h-5" style={{ color: "#00A699" }} />
            {title}
            {!loading && (
              <Badge variant="secondary" className="ml-1">{logs.length}</Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
        <p className="text-sm text-slate-500 mt-1">{description}</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading API logs…
          </div>
        ) : logs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
            <ServerCog className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{emptyText}</p>
            {emptyHint && <p className="text-xs text-slate-400 mt-1">{emptyHint}</p>}
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log, idx) => (
              <LogRow key={log.id || idx} log={log} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
